'use client';

import { useState, type CSSProperties, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCommerceSource,
  type DecimalString,
  type Order,
  type OrderLookup,
} from '@akademi/site-builder-runtime';
import { formatDecimalMoney } from '@akademi/site-builder-blocks';
import { OrderReturns } from './orderReturns';
import { BankTransferNotice, bankTransferOf } from './bankTransfer';

/**
 * The pieces the confirmation page and the tracking page both need.
 *
 * <p>They are the same screen reached two ways: one arrives holding the order,
 * the other has to prove it may see it first. Keeping the rendering in one
 * place is what stops "your order" and "track your order" drifting into two
 * different-looking summaries of identical data.
 */

/**
 * One order, rendered in full.
 *
 * <p>`onChange` is how the page learns of a return raised from here; without it the returns are
 * listed but none can be requested. `proof` is what a guest looked the order up with.
 * An order waiting for its bank transfer carries the payment instructions, until the page
 * showing it draws them itself (`transferNotice={false}`).
 */
export function OrderView({
  order,
  proof,
  onChange,
  transferNotice = true,
}: {
  order: Order;
  proof?: OrderLookup;
  onChange?: (order: Order) => void;
  transferNotice?: boolean;
}) {
  const { t, i18n } = useTranslation('site-builder-blocks');
  const locale = i18n.language || 'en';
  const money = (amount: DecimalString | undefined) =>
    formatDecimalMoney(amount, order.currency ?? '', locale);
  const transfer = transferNotice ? bankTransferOf(order) : null;

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      {transfer && <BankTransferNotice order={order} transfer={transfer} />}
      <dl style={metaStyle}>
        <Meta label={t('commerce.order.number')} value={order.orderNumber} />
        <Meta label={t('commerce.order.status')} value={statusLabel(t, order.status)} />
        {order.fulfilmentStatus && (
          <Meta
            label={t('commerce.order.fulfilment')}
            value={statusLabel(t, order.fulfilmentStatus)}
          />
        )}
        {order.shippingServiceName && (
          <Meta
            label={t('commerce.order.deliveryMethod')}
            value={[order.shippingCarrier, order.shippingServiceName].filter(Boolean).join(' — ')}
          />
        )}
      </dl>

      {order.fulfilmentType === 'COLLECTION' && order.collectionPointName ? (
        <section>
          <h2 style={subheadingStyle}>{t('commerce.order.collectFrom')}</h2>
          <p style={addressStyle}>
            {order.collectionPointName}
            {order.collectionAddressText ? `\n${order.collectionAddressText}` : ''}
          </p>
          {order.collectionCode ? (
            // The code is what hands the goods over, so it is shown large enough to read out at a
            // counter from a phone, and only once the order is actually on the shelf.
            <div style={collectionReadyStyle} role="status">
              <p style={{ margin: 0, fontWeight: 600 }}>{t('commerce.order.readyToCollect')}</p>
              <p style={{ margin: '8px 0 0' }}>
                {t('commerce.order.collectionCode')}{' '}
                <strong style={collectionCodeStyle}>{order.collectionCode}</strong>
              </p>
              {order.collectBy && (
                <p style={{ margin: '8px 0 0' }}>
                  {t('commerce.order.collectBy', { date: formatInstant(order.collectBy, locale) })}
                </p>
              )}
            </div>
          ) : (
            <p style={{ ...mutedTextStyle, marginTop: 8 }}>{t('commerce.order.notReadyYet')}</p>
          )}
        </section>
      ) : (order.deliveryName || order.deliveryAddressText) && (
        <section>
          <h2 style={subheadingStyle}>{t('commerce.order.deliveryTo')}</h2>
          <p style={addressStyle}>
            {order.deliveryName}
            {order.deliveryName && order.deliveryAddressText ? '\n' : ''}
            {order.deliveryAddressText}
          </p>
        </section>
      )}

      <section>
        <h2 style={subheadingStyle}>{t('commerce.order.items')}</h2>
        <ul style={listStyle}>
          {(order.lines ?? []).map((line) => (
            <li key={line.id} style={lineStyle}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 600 }}>{line.description}</span>
                {line.sku && <span style={mutedTextStyle}>{line.sku}</span>}
              </span>
              <span style={mutedTextStyle}>× {line.quantity}</span>
              <span style={{ fontWeight: 600 }}>{money(line.lineTotal)}</span>
            </li>
          ))}
        </ul>
      </section>

      <dl style={totalsStyle}>
        <Total label={t('commerce.cart.subtotal')} value={money(order.subtotal)} />
        {order.discountTotal && order.discountTotal !== '0.00' && (
          <Total label={t('commerce.cart.discount')} value={`-${money(order.discountTotal)}`} />
        )}
        {order.fulfilmentType !== 'COLLECTION' && (
          <Total label={t('commerce.cart.shipping')} value={money(order.shippingTotal)} />
        )}
        <Total label={t('commerce.cart.tax')} value={money(order.taxTotal)} />
        <Total label={t('commerce.cart.total')} value={money(order.total)} emphasis />
      </dl>

      <OrderReturns order={order} proof={proof} onChange={onChange} />
    </div>
  );
}

/**
 * "Find my order": number plus both proofs.
 *
 * <p>Both proofs are required because the backend requires both, and it
 * requires both because an order number is sequential and therefore guessable.
 * A wrong proof is answered exactly like a missing order — so this form says
 * "we could not find that", never "wrong postcode", which would turn it into an
 * oracle for checking whether a given number exists.
 */
export function OrderLookupForm({ onFound }: { onFound: (order: Order, proof: OrderLookup) => void }) {
  const commerce = useCommerceSource();
  const { t } = useTranslation('site-builder-blocks');
  const [orderNumber, setOrderNumber] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [deliveryPostcode, setDeliveryPostcode] = useState('');
  const [looking, setLooking] = useState(false);
  const [notFound, setNotFound] = useState(false);

  async function look(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!commerce?.trackOrder || looking) return;
    setLooking(true);
    setNotFound(false);
    try {
      const proof = { contactEmail: contactEmail.trim(), deliveryPostcode: deliveryPostcode.trim() };
      const order = await commerce.trackOrder(orderNumber.trim(), proof);
      if (order) onFound(order, proof);
      else setNotFound(true);
    } catch {
      // A transport failure is reported the same way, on purpose: the
      // difference between "not found" and "the server errored" is not the
      // shopper's problem and telling them apart leaks whether the number is
      // real.
      setNotFound(true);
    } finally {
      setLooking(false);
    }
  }

  return (
    <form onSubmit={(event) => void look(event)} style={formStyle}>
      {notFound && (
        <p role="alert" style={errorStyle}>
          {t('commerce.order.notFound')}
        </p>
      )}
      <LookupField
        id="order-number"
        label={t('commerce.order.numberLabel')}
        value={orderNumber}
        onChange={setOrderNumber}
        required
      />
      <LookupField
        id="order-email"
        label={t('commerce.order.emailLabel')}
        value={contactEmail}
        onChange={setContactEmail}
        type="email"
        autoComplete="email"
        required
      />
      <LookupField
        id="order-postcode"
        label={t('commerce.order.postcodeLabel')}
        value={deliveryPostcode}
        onChange={setDeliveryPostcode}
        autoComplete="postal-code"
        required
      />
      <button type="submit" disabled={looking} style={buttonStyle}>
        {looking ? t('commerce.order.looking') : t('commerce.order.find')}
      </button>
    </form>
  );
}

function LookupField({
  id,
  label,
  value,
  onChange,
  type = 'text',
  autoComplete,
  required,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  autoComplete?: string;
  required?: boolean;
}) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <label htmlFor={id} style={{ fontSize: 14, fontWeight: 500 }}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
        required={required}
        style={inputStyle}
      />
    </div>
  );
}

/**
 * A status the operator's vocabulary has a word for, or the raw code.
 *
 * <p>Falling back to the code rather than to "Unknown" is deliberate: a status
 * this build has no translation for is still more use to a shopper on the phone
 * to support than a blank.
 */
export function statusLabel(t: (key: string) => string, status: string | undefined): string {
  if (!status) return '';
  const translated = t(`commerce.order.statuses.${status}`);
  return translated.startsWith('commerce.order.statuses.') ? status : translated;
}

function formatInstant(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'full', timeStyle: 'short' }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

function Meta({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div>
      <dt style={mutedTextStyle}>{label}</dt>
      <dd style={{ margin: 0, fontWeight: 600 }}>{value}</dd>
    </div>
  );
}

function Total({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div style={totalRowStyle}>
      <dt style={{ margin: 0, fontWeight: emphasis ? 600 : 400 }}>{label}</dt>
      <dd style={{ margin: 0, fontWeight: emphasis ? 700 : 400 }}>{value}</dd>
    </div>
  );
}

const metaStyle: CSSProperties = {
  display: 'grid',
  gap: 16,
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  margin: 0,
};
const subheadingStyle: CSSProperties = { margin: '0 0 12px', fontSize: 18, fontWeight: 600 };
const addressStyle: CSSProperties = { margin: 0, whiteSpace: 'pre-line' };
const collectionReadyStyle: CSSProperties = {
  marginTop: 12,
  padding: '12px 16px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-success-50, #f0fdf4)',
  border: '1px solid var(--color-success-200, #bbf7d0)',
};
const collectionCodeStyle: CSSProperties = { fontSize: 24, letterSpacing: '0.08em' };
const listStyle: CSSProperties = { listStyle: 'none', margin: 0, padding: 0 };
const lineStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  flexWrap: 'wrap',
  padding: '12px 0',
  borderBottom: '1px solid var(--color-border, #e5e7eb)',
};
const mutedTextStyle: CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: 'var(--color-text-secondary, #6b7280)',
};
const totalsStyle: CSSProperties = {
  display: 'grid',
  gap: 8,
  margin: 0,
  paddingTop: 16,
  borderTop: '1px solid var(--color-border, #e5e7eb)',
};
const totalRowStyle: CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 16 };
const formStyle: CSSProperties = { display: 'grid', gap: 12, maxWidth: 420 };
const inputStyle: CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 'var(--radius-sm, 6px)',
  border: '1px solid var(--color-border, #e5e7eb)',
  font: 'inherit',
};
const buttonStyle: CSSProperties = {
  padding: '12px 24px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-primary, #2563eb)',
  color: 'var(--color-on-primary, #fff)',
  fontWeight: 600,
  border: 'none',
  cursor: 'pointer',
  font: 'inherit',
  justifySelf: 'start',
};
const errorStyle: CSSProperties = {
  margin: 0,
  padding: '12px 16px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-danger-50, #fef2f2)',
  color: 'var(--color-danger-700, #b91c1c)',
};
